import type { BimContractObjectData } from 'gql/graphql';

import * as R from 'ramda';
import { useMemo } from 'react';

export type ItemTableContractObject = {
    children: ItemTableContractObject[];
    hasGuaranteedVolume: 'Да' | 'Нет';
    title: string;
    groupId: number;
    id: number;
    parentId: number;
    isFirstChild?: boolean;
    isLastChild?: boolean;
    parent?: ItemTableContractObject | undefined;
    treeLevel?: number;
    order: number;
};

const sortByParentBaseOrder = R.sortBy(R.prop('order'));

export const updateContractTreeItem = (
    itemToUpdate: ItemTableContractObject,
    object: BimContractObjectData,
): ItemTableContractObject => {
    let seasonsTransform = {};

    seasonsTransform = object.objectSeasonDistributions.reduce((accSeason: Record<string, number>, objSeason) => {
        accSeason = {
            ...accSeason,
            [`season_${objSeason.contractSeason.year}`]: objSeason.seasonValue,
        };

        return accSeason;
    }, {});

    return {
        ...itemToUpdate,
        hasGuaranteedVolume: object.hasGuaranteedVolume ? 'Да' : 'Нет',
        title: object.name,
        ...seasonsTransform,
    };
};

export const getContractTreeItem = (object: BimContractObjectData) => {
    const item = updateContractTreeItem({} as ItemTableContractObject, object);

    return {
        ...item,
        children: [],
        groupId: object.id,
        id: object.id,
        parentId: object.parentId,
        order: object.order,
    };
};

export const useBimItemsContractStructureTable = (bimContractObjectsData: BimContractObjectData[]) =>
    useMemo(
        () =>
            sortByParentBaseOrder(
                bimContractObjectsData?.reduce((acc: ItemTableContractObject[], object) => {
                    if (!object.rootObjectId) {
                        let seasonsTransform = {};

                        seasonsTransform = object.objectSeasonDistributions.reduce(
                            (accSeason: Record<string, number>, objSeason) => {
                                accSeason = {
                                    ...accSeason,
                                    [`season_${objSeason.contractSeason.year}`]: objSeason.seasonValue,
                                };

                                return accSeason;
                            },
                            {},
                        );

                        acc = [
                            ...acc,
                            {
                                children: [],
                                hasGuaranteedVolume: object.hasGuaranteedVolume ? 'Да' : 'Нет',
                                title: object.name,
                                groupId: object.id,
                                id: object.id,
                                parentId: object.parentId,
                                treeLevel: 0,
                                order: object.order,
                                ...seasonsTransform,
                            },
                        ];
                    }

                    if (object.rootObjectId) {
                        const childObject: ItemTableContractObject = {
                            children: [],
                            hasGuaranteedVolume: object?.hasGuaranteedVolume ? 'Да' : 'Нет',
                            title: object.name,
                            groupId: object.rootObjectId,
                            id: object.id,
                            parentId: object.parentId,
                            order: object.order,
                        };

                        const pushChildren = (arrItems: ItemTableContractObject[], usedObject: BimContractObjectData) =>
                            arrItems.map((parentObj) => {
                                if (parentObj.id === usedObject.parentId) {
                                    parentObj.children = sortByParentBaseOrder([...parentObj.children, childObject]);
                                } else {
                                    pushChildren(parentObj.children, usedObject);
                                }

                                return parentObj;
                            });

                        acc = pushChildren(acc, object);
                    }

                    return acc;
                }, []) || [],
            ),
        [bimContractObjectsData],
    );
